import React from "react";
import { Box, Autocomplete, TextField, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { EventFiltersProps } from "../interfaces/eventFilters";
import { DAY_OPTIONS } from "../utils/const";
import themestyle from "../theme";

const EventFilters: React.FC<EventFiltersProps> = ({
  instructorValue,
  dayValue,
  amountValue,
  bookingValue,
  onInstructorChange,
  onDayChange,
  onAmountChange,
  onBookingChange,
  instructorOptions = [],
  hideDayFilter = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "32px",
      backgroundColor: themestyle.colors.white,
      fontSize: isMobile ? 12 : 14,
      minHeight: isMobile ? 45 : 52,
      px: isMobile ? 1 : 2,
      "& fieldset": {
        borderWidth: "1px",
        borderColor: themestyle.colors.bordercolor,
      },
      "&:hover fieldset": {
        borderColor: themestyle.colors.activeGreen,
        borderWidth: "1px",
      },
      "&.Mui-focused fieldset": {
        borderColor: themestyle.colors.activeGreen,
        borderWidth: "1px",
      },
    },
    "& .MuiInputBase-input": {
      height: isMobile ? "25px" : "auto",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: isMobile ? 0.3 : 1.5,
        boxSizing: "border-box",
        width: isMobile ? "95%" : "100%",
        mx: isMobile ? "auto" : 0,
      }}
    >
      <Autocomplete
        size={isMobile ? "small" : "medium"}
        options={instructorOptions}
        getOptionLabel={(option: any) =>
          typeof option === "string" ? option : option.fullname || ""
        }
        value={instructorValue}
        onChange={(_, newValue: any) => {
          onInstructorChange(typeof newValue === "string" ? newValue : newValue?.fullname || null);
        }}
        disableClearable={false}
        forcePopupIcon={isMobile ? !instructorValue : true}
        sx={{ flex: 1, minWidth: 0 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder={isMobile ? "Instructor" : "Filter by Instructor"}
            sx={inputSx}
          />
        )}
        slotProps={{
          listbox: {
            sx: {
              fontSize: isMobile ? 11 : 14,
              "& .MuiAutocomplete-option": {
                padding: isMobile ? "4px 8px" : "6px 16px",
                minHeight: isMobile ? "auto" : undefined,
              },
            },
          },
          popper: isMobile
            ? {
                placement: "bottom-end",
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 4],
                    },
                  },
                ],
              }
            : undefined,
        }}
      />

      {onAmountChange && (
        <Autocomplete
          size={isMobile ? "small" : "medium"}
          options={["All Amounts", "Free", "Under ₹500", "₹500 - ₹999", "₹1000 & Above"]}
          value={amountValue}
          onChange={(_, newValue) => {
            onAmountChange(newValue);
          }}
          disableClearable={false}
          forcePopupIcon={isMobile ? !amountValue : true}
          sx={{ flex: 1, minWidth: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label=""
              placeholder={isMobile ? "Amount" : "Filter by Amount"}
              sx={inputSx}
            />
          )}
          slotProps={{
            listbox: {
              sx: {
                fontSize: isMobile ? 11 : 14,
                "& .MuiAutocomplete-option": {
                  padding: isMobile ? "4px 8px" : "6px 16px",
                  minHeight: isMobile ? "auto" : undefined,
                },
              },
            },
            popper: isMobile
              ? {
                  placement: "bottom-end",
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 4],
                      },
                    },
                  ],
                }
              : undefined,
          }}
        />
      )}

      {onBookingChange && (
        <Autocomplete
          size={isMobile ? "small" : "medium"}
          options={["All Events", "Joined", "Not Joined"]}
          value={bookingValue}
          onChange={(_, newValue) => {
            onBookingChange(newValue);
          }}
          disableClearable={false}
          forcePopupIcon={isMobile ? !bookingValue : true}
          sx={{ flex: 1, minWidth: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label=""
              placeholder={isMobile ? "Bookings" : "Filter by Bookings"}
              sx={inputSx}
            />
          )}
          slotProps={{
            listbox: {
              sx: {
                fontSize: isMobile ? 11 : 14,
                "& .MuiAutocomplete-option": {
                  padding: isMobile ? "4px 8px" : "6px 16px",
                  minHeight: isMobile ? "auto" : undefined,
                },
              },
            },
            popper: isMobile
              ? {
                  placement: "bottom-end",
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 4],
                      },
                    },
                  ],
                }
              : undefined,
          }}
        />
      )}

      {!hideDayFilter && (
        <Autocomplete
          size={isMobile ? "small" : "medium"}
          options={DAY_OPTIONS}
          value={dayValue}
          onChange={(_, newValue) => {
            onDayChange(newValue);
          }}
          disableClearable={false}
          forcePopupIcon={isMobile ? !dayValue : true}
          sx={{ flex: 1, minWidth: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label=""
              placeholder={isMobile ? "Day" : "Filter by Day"}
              sx={inputSx}
            />
          )}
          slotProps={{
            listbox: {
              sx: {
                fontSize: isMobile ? 11 : 14,
                "& .MuiAutocomplete-option": {
                  padding: isMobile ? "4px 8px" : "6px 16px",
                  minHeight: isMobile ? "auto" : undefined,
                },
              },
            },
            popper: isMobile
              ? {
                  placement: "bottom-end",
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 4],
                      },
                    },
                  ],
                }
              : undefined,
          }}
        />
      )}
    </Box>
  );
};

export default EventFilters;
